import { useState, useEffect, useMemo, useRef, RefCallback } from "react";
import { transformImageUrl, getAlternateBucketUrl, getBucketFromUrl, togglePublicPrefix } from "@/utils/image.util";
import { DEFAULT_COVER_IMAGE } from "@/constants/common";
import { debug } from "@/utils/logger";

const DEFAULT_HERO_IMAGE = DEFAULT_COVER_IMAGE;

export interface ImageValidationResult {
  url: string;
  label: string;
  success: boolean;
  originalUrl?: string;
}

export interface ImageChainMetadata {
  chain: ImageValidationResult[];
  finalImage: string;
  finalSource: string; // Which step in the chain succeeded (or 'default')
}

interface UseHeroImageProps {
  heroImage?: string | { primary: string; fallback?: string }; // Can be string or HeroImageResult object
  heroImageSecondary?: string;
  defaultImage?: string;
  lazy?: boolean;
  trackChain?: boolean; // If true, track validation chain results
}

/**
 * Custom hook for handling hero image loading with fallback to secondary
 * Tries images in priority order: hero -> hero_secondary -> default
 */
export const useHeroImage = ({
  heroImage = "",
  heroImageSecondary = "",
  defaultImage = DEFAULT_HERO_IMAGE,
  lazy = false,
  trackChain = false,
}: UseHeroImageProps): 
  | string 
  | { resolvedHeroImage: string; containerRef: RefCallback<HTMLElement> }
  | { resolvedHeroImage: string; containerRef: RefCallback<HTMLElement>; chainMetadata: ImageChainMetadata | null }
  | { resolvedHeroImage: string; chainMetadata: ImageChainMetadata | null } => {
  const [resolvedHeroImage, setResolvedHeroImage] = useState<string>(defaultImage);
  const [isVisible, setIsVisible] = useState<boolean>(!lazy);
  const [chainMetadata, setChainMetadata] = useState<ImageChainMetadata | null>(null);
  const elementRef = useRef<HTMLElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Extract primary and fallback URLs from heroImage (can be string or object)
  const { primaryUrl, fallbackUrl } = useMemo(() => {
    if (!heroImage) {
      return { primaryUrl: "", fallbackUrl: heroImageSecondary || "" };
    }
    
    // Handle HeroImageResult object
    if (typeof heroImage === "object" && "primary" in heroImage) {
      const primary = transformImageUrl(heroImage.primary);
      const fallback = heroImage.fallback 
        ? transformImageUrl(heroImage.fallback)
        : heroImageSecondary || "";
      const result = {
        primaryUrl: typeof primary === "string" ? primary : heroImage.primary,
        fallbackUrl: typeof fallback === "string" ? fallback : (heroImage.fallback || heroImageSecondary || "")
      };
      debug('[useHeroImage] Extracted URLs from HeroImageResult', {
        inputPrimary: heroImage.primary,
        inputFallback: heroImage.fallback,
        extractedPrimary: result.primaryUrl,
        extractedFallback: result.fallbackUrl,
        heroImageSecondary,
      });
      return result;
    }
    
    // Handle string (backward compatibility)
    const transformed = transformImageUrl(heroImage);
    return {
      primaryUrl: typeof transformed === "string" ? transformed : heroImage,
      fallbackUrl: heroImageSecondary || ""
    };
  }, [heroImage, heroImageSecondary]);

  // Normalize URLs (handle legacy S3 buckets)
  const normalizedHeroImage = useMemo(() => {
    return primaryUrl;
  }, [primaryUrl]);

  const normalizedHeroImageSecondary = useMemo(() => {
    return fallbackUrl;
  }, [fallbackUrl]);

  // Ref callback to attach to container element
  const containerRef: RefCallback<HTMLElement> = (element) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    elementRef.current = element;

    if (lazy && element && typeof window !== 'undefined' && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setIsVisible(true);
              observer.disconnect();
              observerRef.current = null;
            }
          });
        },
        {
          rootMargin: '50px',
          threshold: 0.01,
        }
      );

      observer.observe(element);
      observerRef.current = observer;
    } else if (!lazy) {
      setIsVisible(true);
    }
  };

  // Cleanup observer on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, []);

  // Consolidated fallback chain: hero -> hero_secondary -> default
  useEffect(() => {
    if (!isVisible) return;
    let isCancelled = false;

    const chain: ImageValidationResult[] = [];
    const primaryOriginalUrl: string = typeof heroImage === "string" ? heroImage : (typeof heroImage === "object" && heroImage?.primary ? heroImage.primary : "");
    const urls: Array<{ url: string; label: string; originalUrl: string }> = [
      { url: normalizedHeroImage, label: "Primary", originalUrl: primaryOriginalUrl },
      { url: normalizedHeroImageSecondary, label: "Secondary", originalUrl: heroImageSecondary },
    ].filter((item): item is { url: string; label: string; originalUrl: string } => !!item.url);

    if (urls.length === 0) {
      const finalDefault = defaultImage;
      setResolvedHeroImage(finalDefault);
      if (trackChain) {
        setChainMetadata({
          chain: [{ url: finalDefault, label: "Default", success: true }],
          finalImage: finalDefault,
          finalSource: "default"
        });
      }
      return;
    }

    const tryImages = async () => {
      for (const { url, label, originalUrl } of urls) {
        // Skip validation for Instagram/TikTok CDN URLs (they have CORS restrictions)
        // but work fine in actual <img> tags
        const isExternalCDN = url.includes('cdninstagram.com') ||
                             url.includes('tiktokcdn.com') ||
                             url.includes('scontent');

        // Helper function to try loading an image URL
        const tryLoadImage = async (imageUrl: string): Promise<boolean> => {
          if (isExternalCDN) return true;
          
          return new Promise<boolean>((resolve) => {
            const img = new window.Image();
            let timeoutId: number | null = null;
            let isResolved = false;

            const handleLoad = () => {
              if (isResolved) return;
              isResolved = true;
              cleanup();
              resolve(true);
            };

            const handleError = () => {
              if (isResolved) return;
              isResolved = true;
              cleanup();
              resolve(false);
            };

            const cleanup = () => {
              if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
              }
              img.onload = null;
              img.onerror = null;
              img.src = '';
            };

            img.onload = handleLoad;
            img.onerror = handleError;

            // 10 second timeout to prevent hanging
            timeoutId = window.setTimeout(() => {
              if (isResolved) return;
              isResolved = true;
              cleanup();
              resolve(false);
            }, 10000);

            img.src = imageUrl;
          });
        };

        // Try primary URL first
        let loaded = await tryLoadImage(url);
        let finalUrl = url;
        let finalLabel = label;
        
        // If primary URL failed and it's an S3 URL, try path variations and alternate bucket
        if (!loaded && !isExternalCDN && url.includes('.s3.') && url.includes('.amazonaws.com')) {
          // Step 1: Try toggling public/ prefix (prod has both old and new structure)
          const hasPublicPrefix = url.includes('/public/');
          const toggledPathUrl = togglePublicPrefix(url, !hasPublicPrefix);
          
          if (toggledPathUrl !== url) {
            loaded = await tryLoadImage(toggledPathUrl);
            if (loaded) {
              finalUrl = toggledPathUrl;
              finalLabel = `${label} (${hasPublicPrefix ? 'no public/' : 'public/'} prefix)`;
            }
          }
          
          // Step 2: If still failed, try alternate bucket with original path
          if (!loaded) {
            const currentBucket = getBucketFromUrl(url);
            const alternateBucket = currentBucket === 'dev' ? 'prod' : currentBucket === 'prod' ? 'dev' : null;
            const alternateUrl = alternateBucket ? getAlternateBucketUrl(url, alternateBucket) : '';
            
            if (alternateUrl) {
              loaded = await tryLoadImage(alternateUrl);
              if (loaded) {
                finalUrl = alternateUrl;
                finalLabel = `${label} (${alternateBucket} bucket)`;
              }
            }
          }
          
          // Step 3: If still failed, try alternate bucket with toggled path
          if (!loaded && toggledPathUrl !== url) {
            const currentBucket = getBucketFromUrl(toggledPathUrl);
            const alternateBucket = currentBucket === 'dev' ? 'prod' : currentBucket === 'prod' ? 'dev' : null;
            const alternateToggledUrl = alternateBucket ? getAlternateBucketUrl(toggledPathUrl, alternateBucket) : '';
            
            if (alternateToggledUrl) {
              loaded = await tryLoadImage(alternateToggledUrl);
              if (loaded) {
                finalUrl = alternateToggledUrl;
                finalLabel = `${label} (${alternateBucket} bucket, ${hasPublicPrefix ? 'no public/' : 'public/'} prefix)`;
              }
            }
          }
          
          // If any variation worked, update the result
          if (loaded) {
            chain.push({
              url,
              label,
              success: false,
              originalUrl: originalUrl,
            });
            const result: ImageValidationResult = {
              url: finalUrl,
              label: finalLabel,
              success: true,
              originalUrl: originalUrl,
            };
            chain.push(result);
            
            setResolvedHeroImage(finalUrl);
            if (trackChain) {
              setChainMetadata({
                chain,
                finalImage: finalUrl,
                finalSource: finalLabel.toLowerCase().replace(/\s+/g, '-')
              });
            }
            return;
          }
        }

        if (isCancelled) return;

        const originalUrlStr = typeof originalUrl === "string" ? originalUrl : undefined;
        const result: ImageValidationResult = {
          url,
          label,
          success: loaded,
          originalUrl: originalUrlStr,
        };
        chain.push(result);

        if (loaded) {
          setResolvedHeroImage(url);
          if (trackChain) {
            setChainMetadata({
              chain,
              finalImage: url,
              finalSource: label.toLowerCase()
            });
          }
          return;
        }
      }

      if (!isCancelled) {
        const finalDefault = defaultImage;
        setResolvedHeroImage(finalDefault);
        chain.push({
          url: finalDefault,
          label: "Default",
          success: true,
        });
        if (trackChain) {
          setChainMetadata({
            chain,
            finalImage: finalDefault,
            finalSource: "default"
          });
        }
      }
    };

    tryImages();

    return () => {
      isCancelled = true;
    };
  }, [normalizedHeroImage, normalizedHeroImageSecondary, defaultImage, isVisible, trackChain, heroImage, heroImageSecondary]);

  // Return based on what's requested
  if (lazy && trackChain) {
    return { resolvedHeroImage, containerRef, chainMetadata: chainMetadata || null };
  }
  if (lazy) {
    return { resolvedHeroImage, containerRef };
  }
  if (trackChain) {
    return { resolvedHeroImage, chainMetadata: chainMetadata || null };
  }
  return resolvedHeroImage;
};
